import { useMemo, useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  CardHeader,
  ToggleButtonGroup,
  ToggleButton,
  Typography,
  Skeleton,
  Alert,
  Button,
  Divider,
} from '@mui/material';
import { useDeviceUptime } from '@/hooks/use-analytics';
import { colors } from '@/theme/tokens';
import type { AnalyticsWindow } from '@/lib/analytics-range';

interface DeviceUptimeProps {
  deviceId: string;
  /**
   * Shared Analytics-tab window. When supplied the panel drops its Card chrome,
   * its own 7d/30d toggle and the big percentage headline — the container's
   * verdict strip carries the number — leaving the online/offline breakdown.
   */
  window?: AnalyticsWindow;
}

type Period = '7d' | '30d';

function formatDuration(ns: number): string {
  // Go's time.Duration is serialised as nanoseconds in JSON
  const totalSeconds = Math.floor(ns / 1e9);
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;
  if (hours > 0) return `${hours}h ${minutes}m`;
  if (minutes > 0) return `${minutes}m ${seconds}s`;
  return `${seconds}s`;
}

function periodToDates(period: Period): { start: string; end: string } {
  const end = new Date();
  const start = new Date(end);
  const days = period === '7d' ? 7 : 30;
  start.setDate(start.getDate() - days);
  return { start: start.toISOString(), end: end.toISOString() };
}

export function DeviceUptime({ deviceId, window: win }: DeviceUptimeProps) {
  const [period, setPeriod] = useState<Period>('7d');
  const embedded = !!win;
  // Pin the window per period so the query key is stable across renders;
  // computing dates inline causes infinite refetch loops.
  const own = useMemo(() => periodToDates(period), [period]);
  const start = win?.start ?? own.start;
  const end = win?.end ?? own.end;

  const { data, isLoading, isError, refetch } = useDeviceUptime(deviceId, start, end);

  // The backend clamps the window to the device's enrollment, so a freshly
  // enrolled device isn't judged on time before it existed. Reflect the real
  // measured span instead of always claiming the full selected period.
  const windowLabel = useMemo(() => {
    const requestedMs = win ? win.spanMs : (period === '7d' ? 7 : 30) * 86_400_000;
    const full = win
      ? win.label.replace(/^Last /, '')
      : period === '7d' ? '7 days' : '30 days';
    if (!data) return full;
    const spanMs = new Date(data.end_time).getTime() - new Date(data.start_time).getTime();
    if (spanMs > 0 && spanMs < requestedMs * 0.95) {
      const hrs = Math.max(1, Math.round(spanMs / 3_600_000));
      const span = hrs < 48 ? `${hrs}h` : `${Math.round(hrs / 24)} days`;
      return `${span} (since enrollment)`;
    }
    return full;
  }, [data, period, win]);

  const Shell = (embedded ? Box : Card) as React.ElementType;
  const shellProps = embedded ? {} : { variant: 'outlined' as const };
  const Body = (embedded ? Box : CardContent) as React.ElementType;

  return (
    <Shell {...shellProps}>
      {!embedded && (
        <CardHeader
          title="Uptime"
          titleTypographyProps={{ variant: 'h6', fontWeight: 600 }}
          action={
            <ToggleButtonGroup
              value={period}
              exclusive
              onChange={(_, val) => val && setPeriod(val)}
              size="small"
            >
              <ToggleButton value="7d">7 Days</ToggleButton>
              <ToggleButton value="30d">30 Days</ToggleButton>
            </ToggleButtonGroup>
          }
        />
      )}
      <Body>
        {isLoading && (
          // Reserve the headline + stats-row height so switching periods
          // (or first load) doesn't shift the surrounding tab layout.
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Skeleton variant="text" width={260} height={72} />
            <Divider />
            <Box sx={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
              <Skeleton variant="text" width={80} height={48} />
              <Skeleton variant="text" width={80} height={48} />
              <Skeleton variant="text" width={80} height={48} />
            </Box>
          </Box>
        )}

        {isError && (
          <Alert
            severity="error"
            variant="outlined"
            action={
              <Button color="inherit" size="small" onClick={() => refetch()}>
                Retry
              </Button>
            }
          >
            Failed to load uptime data.
          </Alert>
        )}

        {!isLoading && !isError && data && (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            {/* Informational only. A powered-off machine is normal for a fitness
                device (nobody is working out) — NOT a fault — so no health tier,
                no "Critical", no alarm colors. */}
            {/* Embedded, the percentage leads the tab from the verdict strip —
                repeating it here at h2 would just be the same number twice. */}
            {!embedded && (
              <>
                <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1, flexWrap: 'wrap' }}>
                  <Typography variant="h2" fontWeight={700} color="text.primary">
                    {data.uptime_percent.toFixed(1)}%
                  </Typography>
                  <Typography variant="body1" color="text.secondary">
                    powered on, {windowLabel}
                  </Typography>
                </Box>
                <Divider />
              </>
            )}

            {/* Stats row */}
            <Box sx={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
              <Box>
                <Typography variant="caption" color="text.secondary">
                  Online Time
                </Typography>
                <Typography variant="body1" fontWeight={600} sx={{ color: colors.success.main }}>
                  {/* time.Duration is serialised as nanoseconds; formatDuration divides by 1e9 */}
                  {formatDuration(data.total_online_nanos)}
                </Typography>
              </Box>
              <Box>
                <Typography variant="caption" color="text.secondary">
                  Offline Time
                </Typography>
                <Typography variant="body1" fontWeight={600} color="text.secondary">
                  {formatDuration(data.total_offline_nanos)}
                </Typography>
              </Box>
              <Box>
                <Typography variant="caption" color="text.secondary">
                  Connection runs
                </Typography>
                <Typography variant="body1" fontWeight={600}>
                  {data.session_count}
                </Typography>
                <Typography variant="caption" color="text.disabled" sx={{ display: 'block' }}>
                  not workouts
                </Typography>
              </Box>
            </Box>
          </Box>
        )}

        {!isLoading && !isError && !data && (
          <Alert severity="info" variant="outlined">
            No uptime data available for this device in the selected period.
          </Alert>
        )}
      </Body>
    </Shell>
  );
}

export default DeviceUptime;
